// Heavy authenticated pages are code-split via lazyRouteComponent: their
// bundles (charts, admin tables, the onboarding flow) download on navigation
// instead of shipping in the initial payload. Auth pages stay EAGER so the
// pre-login critical path costs no extra round-trip.
import {
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { CallbackLayout } from '../layouts/CallBackLayout';
import { authRoute, requireSecondFactor, rootRoute } from './roots';
import { ensureAccess } from '@/features/Auth/accessSession';
import { executionSection } from '@/features/Execution/sections';
import { LoginPage } from '@/features/Auth/views/LoginPage';
import { SignUpPage } from '@/features/Auth/views/SignUpPage';
import { ForgotPasswordPage } from '@/features/Auth/views/ForgotPasswordPage';
import { VerifyEmailPage } from '@/features/Auth/views/VerifyEmailPage';
import { AuthActionPage } from '@/features/Auth/views/AuthActionPage';
import { SecuritySetupPage } from '@/features/Auth/views/SecuritySetupPage';
import { AcceptInvitationPage } from '@/features/Auth/views/AcceptInvitationPage';
import { useUserStore } from '@/entities/user/store';
import { userProfileRoute } from '@/features/Header/UserProfile/user-profile.routes';
import {
  benchmarkDetailRoute,
  liveModelRoute,
  rebalanceScheduleRoute,
  securitySelectionStepRoute,
  smartStrategyDefinitionFormRoute,
  strategiesDashboardRoute,
  strategiesRoute,
  strategyReportRoute,
  weightingMethodRoute,
} from '@/features/Strategies/strategies.routes.tsx';
import { startingUniverseRoute } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/StartingUniverse/route';
import * as TanStackQueryProvider from '@/shared/integrations/tanstack-query/root-provider';
import { config } from '@/shared/config/env';
import { PlaceholderPage } from '@/shared/components/PlaceholderPage';
import { RoutePending } from '@/shared/components/RoutePending';
import { NotFound } from '@/shared/components/NotFound';
import { RouteErrorFallback } from '@/shared/components/RouteErrorFallback';
import { portalRoutes } from '@/features/ClientPortal/portal.routes';

const indexRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/',
  // /dashboard is now the Soteria-prompt landing — the first thing every
  // authenticated user sees after sign-in or the welcome page. It used
  // to redirect to /strategies, which was fine when the dashboard was
  // a stub but no longer matches where we want users to start.
  beforeLoad: () => {
    throw redirect({
      to: '/dashboard',
      replace: true,
    });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login',
  component: LoginPage,
});

const accessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'access',
  component: lazyRouteComponent(
    () => import('@/features/Auth/views/AccessChooserPage'),
    'AccessChooserPage',
  ),
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'signup',
  component: SignUpPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'forgot-password',
  component: ForgotPasswordPage,
});

// A recipient verifies an emailed code using a separate, transcript-scoped
// cookie. This public sibling must never run the advisor authentication guard
// or mount the CRM shell, even if the same browser has an advisor session.
const guestTranscriptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'meeting-transcript',
  component: lazyRouteComponent(
    () =>
      import('@/features/MeetingBots/transcripts/public/GuestTranscriptPage'),
    'GuestTranscriptPage',
  ),
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'verify-email',
  component: VerifyEmailPage,
});

// Landing route for the links Identity Platform emails (verify email, password
// reset, email-change recovery). Registered as the Firebase project's custom
// "action URL" so those links stop landing on Google's unbranded handler page.
// Firebase controls the query string: ?mode=…&oobCode=…&continueUrl=…
const authActionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth/action',
  component: AuthActionPage,
});

// Mandatory two-factor setup. A sibling of /login rather than a child of the
// authenticated tree: the `_auth` guard is what redirects here, so nesting it
// inside would loop.
const securitySetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'security-setup',
  component: SecuritySetupPage,
});

const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'accept-invitation',
  component: AcceptInvitationPage,
});

const callBackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'callback',
  component: CallbackLayout,
});

// Where Microsoft redirects the calendar-consent POPUP. Hangs off rootRoute,
// NOT authRoute, and that is the point: the popup opens with no session state
// of its own, and an auth guard here would bounce it to the login screen with
// the authorization code still in the URL. The page itself is inert — it
// relays the code to its opener and closes — so there is nothing here to
// protect. Path is duplicated from MICROSOFT_CALENDAR_CALLBACK_PATH because a
// TanStack route path must be a literal; the constant is what Azure and the
// authorize URL agree on.
const microsoftCalendarCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth/microsoft/calendar-callback',
  component: lazyRouteComponent(
    () => import('@/features/Settings/calendar/CalendarCallbackPage'),
    'CalendarCallbackPage',
  ),
});

// Google lands here now rather than on `postmessage`.
//
// The GIS popup code client used the literal string `postmessage` as its
// redirect URI, which is not a URL — it tells Google to hand the code back
// through the popup. That client cannot send `access_type=offline` or
// `prompt=consent` (they are not fields of its config), so re-authorising after
// a revocation returned no refresh token and the reconnect path could never
// repair a broken connection. The server builds the URL now, which means Google
// needs a real redirect URI, which means this route.
//
// PATH REGISTRATION IS A PREREQUISITE: this URI must be added to the Google
// OAuth client's authorised redirect URIs, for every origin, before this ships.
const googleCalendarCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth/google/calendar-callback',
  component: lazyRouteComponent(
    () => import('@/features/Settings/calendar/CalendarCallbackPage'),
    'CalendarCallbackPage',
  ),
});

// The mailbox pair. SEPARATE PATHS FROM THE CALENDAR'S, deliberately: reading
// a person's mail is a wider grant than reading their calendar, each redirect
// URI is registered with the provider, and one path serving both grants would
// mean a registration changed for one silently applies to the other.
//
// PATH REGISTRATION IS A PREREQUISITE, the same as the Google calendar route
// above: both URIs must be added to the provider registrations, for every
// origin, before this ships.
//
// Not authRoute, for the reason the calendar routes give: the popup opens with
// no session state of its own and a guard would bounce it to the login screen
// with the authorization code still in the URL.
const microsoftMailCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth/microsoft/mail-callback',
  component: lazyRouteComponent(
    () => import('@/features/Clients/mailbox/MailboxCallbackPage'),
    'MailboxCallbackPage',
  ),
});

// The Teams Phone consent callback. Microsoft sends a tenant administrator here
// after the admin-consent screen, in a popup, with `tenant`, `state` and
// `admin_consent` and no authorization code. The page is inert and relays them
// to the opener, which calls `GET /v1/phone/callback` with the admin's session,
// so it sits outside authRoute for the same reason the mailbox pair does.
//
// PATH REGISTRATION IS A PREREQUISITE: the redirect URI
// `<origin>/auth/microsoft/phone-callback` must be added to the Microsoft phone
// app registration for every origin, and the path is in the edge's COOP
// exemption (nginx.conf and Caddyfile) or the popup loses its opener.
const microsoftPhoneCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth/microsoft/phone-callback',
  component: lazyRouteComponent(
    () => import('@/features/Phone/PhoneCallbackPage'),
    'PhoneCallbackPage',
  ),
});

// The SharePoint connect callback. Same shape and rules as the phone callback
// above (outside authRoute, inert, relays to the opener, which POSTs it to
// `/v1/sharepoint/callback`). PREREQUISITE: `<origin>/auth/microsoft/sharepoint-callback`
// registered on the SharePoint Entra app for every origin, and the path is in the
// edge's COOP exemption (nginx.conf and Caddyfile).
const microsoftSharepointCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth/microsoft/sharepoint-callback',
  component: lazyRouteComponent(
    () => import('@/features/SharePoint/SharepointCallbackPage'),
    'SharepointCallbackPage',
  ),
});

// Where Microsoft redirects the SIGN-UP popup (`/auth/microsoft-signup`). Same
// rules as the callbacks above: outside authRoute (the popup has no session),
// inert (relays the id_token to its opener and closes), and the path is in the
// edge's COOP exemption. Prerequisite: `<origin>/auth/microsoft/signin-callback`
// registered as a redirect URI on the Entra app, with ID tokens enabled.
const microsoftSigninCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth/microsoft/signin-callback',
  component: lazyRouteComponent(
    () => import('@/features/Auth/views/MicrosoftSigninCallbackPage'),
    'MicrosoftSigninCallbackPage',
  ),
});

const googleMailCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth/google/mail-callback',
  component: lazyRouteComponent(
    () => import('@/features/Clients/mailbox/MailboxCallbackPage'),
    'MailboxCallbackPage',
  ),
});

// The onboarding route sits as a sibling of /login and /strategies —
// it requires the user to be authenticated but renders WITHOUT the
// sidebar + header chrome, because the Figma onboarding takes over the
// full viewport. Anyone who hits /onboarding without a session gets
// pushed to /login first.
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'onboarding',
  beforeLoad: async ({ context }) => {
    const isAuthenticated = useUserStore.getState().isAuthenticated;
    if (!isAuthenticated) {
      throw redirect({ to: '/login' });
    }
    // The same mandatory two-factor gate authRoute enforces.
    //
    // This route is a SIBLING of authRoute, not a child -- it renders without
    // the sidebar chrome, so it hangs off the root -- and the gate used to live
    // inline in authRoute alone. The check above is a client-side store flag
    // and was the only thing here, so this door was unguarded, and the signup
    // flow walks straight through it: confirming an address sets
    // `biasafe_onboarding_pending`, postLoginPath() then returns '/onboarding',
    // and a new account created its whole workspace with no second factor. The
    // gate was only met later, on the first visit to a route that happened to
    // sit under authRoute.
    if (!(await ensureAccess())) throw redirect({ to: '/access' });
    await requireSecondFactor(context.queryClient);
  },
  component: lazyRouteComponent(
    () => import('@/features/Onboarding/OnboardingPage'),
    'OnboardingPage',
  ),
});

// The public, self-serve KYC/KYB onboarding flow (onboarding.biasafe.ai).
// No session required — a client opens it from an emailed link, uploads their
// documents, and completes their NBIN / Cadence / IBKR forms. Renders fully
// standalone (its own purple-branded chrome, "Powered by Sahl").
const hostedOnboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'onboard',
  component: lazyRouteComponent(
    () => import('@/features/Onboarding/HostedOnboarding'),
    'HostedOnboarding',
  ),
});

// The public collection page. Reached only from an emailed link carrying a
// token; no session, and no session is created. Whoever opens it is the person
// a dossier's answers belong to — usually the anchor, who holds the answers for
// every application in it. NOTHING IS SIGNED HERE; that is a different link.
const caseIntakeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'case-intake',
  component: lazyRouteComponent(
    () => import('@/features/CaseIntake/CaseIntakePage'),
    'CaseIntakePage',
  ),
});

// The public dossier signing page. Reached only from an emailed link carrying
// a token; no session. Two steps and no third: read the frozen documents of ONE
// application, then sign them. Nothing is filled in here — that was the
// collection link, and it is deliberately a different one.
const caseSignRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'case-sign',
  component: lazyRouteComponent(
    () => import('@/features/CaseSign/CaseSignPage'),
    'CaseSignPage',
  ),
});

// The public third-party signing page. Reached only from an emailed link
// carrying a token; no session, and no session is created. Whoever opens it is
// a spouse, a witness, an attorney — somebody the client's paperwork names,
// with a line on a form and no other way to reach it.
const cosignRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'cosign',
  component: lazyRouteComponent(
    () => import('@/features/CoSign/CoSignPage'),
    'CoSignPage',
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'dashboard',
  component: lazyRouteComponent(
    () => import('@/features/Home/HomePage'),
    'HomePage',
  ),
});

const portfolioRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'portfolio',
  component: lazyRouteComponent(
    () => import('@/features/MyPortfolio/PortfolioHoldings'),
    'PortfolioHoldings',
  ),
});

const meetingBotsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'meeting-bots',
  // `?tab=calendar` opens the week view, so it can be linked to and survives a
  // reload. Anything else is the bots view. The input is `{ tab?: unknown }`
  // and not a Record for the reason the portal's `thread` gives: a plain
  // `<Link to="/meeting-bots">`, the sidebar's included, must stay valid.
  validateSearch: (search: { tab?: unknown }): { tab?: 'calendar' } =>
    search.tab === 'calendar' ? { tab: 'calendar' } : {},
  component: lazyRouteComponent(
    () => import('@/features/MeetingBots/MeetingBotsPage'),
    'MeetingBotsPage',
  ),
});

const executionRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'execution',
  validateSearch: (search: {
    tab?: unknown;
  }): { tab?: ReturnType<typeof executionSection> | 'connections' } => ({
    tab:
      search.tab === 'connections'
        ? 'connections'
        : executionSection(search.tab),
  }),
  beforeLoad: ({ search }) => {
    // Preserve saved links after moving connection diagnostics into Settings.
    if (search.tab === 'connections')
      throw redirect({
        to: '/settings',
        search: { tab: 'connections' },
        replace: true,
      });
  },
  component: lazyRouteComponent(
    () => import('@/features/Execution/ExecutionPage'),
    'ExecutionPage',
  ),
});

const securityDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'securities/$performanceId',
  component: lazyRouteComponent(
    () => import('@/features/Securities/SecurityDetailPage'),
    'SecurityDetailPage',
  ),
});

const instrumentRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'instruments/$conid',
  component: lazyRouteComponent(
    () => import('@/features/Instruments/InstrumentDetailPage'),
    'InstrumentDetailPage',
  ),
});

const integrationsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'integrations',
  component: lazyRouteComponent(
    () => import('@/features/Integrations/IntegrationsPage'),
    'IntegrationsPage',
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'settings',
  validateSearch: (search: { tab?: unknown }): { tab?: string } => ({
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
  component: lazyRouteComponent(
    () => import('@/features/Settings/views/SettingsPage'),
    'SettingsPage',
  ),
});

/**
 * The appearance workbench, at /dev/appearance. Dev builds only.
 *
 * Every primitive in the app on one page, so the four combinations of theme
 * and corner style can be looked at together rather than hunted for across
 * feature screens. It is also what the Playwright appearance baselines
 * screenshot, which is why it is a route at all rather than a Storybook story
 * this repo has no runner for.
 *
 * `import.meta.env.DEV` is a build-time constant that Vite replaces with
 * `false` in a production build, so the array below collapses and Rollup drops
 * the lazy import with it. Nothing ships.
 */
const devGalleryRoutes = import.meta.env.DEV
  ? [
      createRoute({
        getParentRoute: () => authRoute,
        path: 'dev/appearance',
        component: lazyRouteComponent(
          () => import('@/features/DevGallery/AppearanceGallery'),
          'AppearanceGallery',
        ),
      }),
    ]
  : [];

const teamRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'team',
  component: lazyRouteComponent(
    () => import('@/features/Team/views/TeamPage'),
    'TeamPage',
  ),
});

const docsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'docs',
  validateSearch: (search: { article?: unknown }): { article?: string } => ({
    article: typeof search.article === 'string' ? search.article : undefined,
  }),
  component: lazyRouteComponent(
    () => import('@/features/Knowledge/KnowledgePage'),
    'AdvisorDocumentationPage',
  ),
});

const changelogRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'changelog',
  validateSearch: (search: { article?: unknown }): { article?: string } => ({
    article: typeof search.article === 'string' ? search.article : undefined,
  }),
  component: lazyRouteComponent(
    () => import('@/features/Changelog/ChangelogPage'),
    'AdvisorChangelogPage',
  ),
});

const helpRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'help',
  component: () =>
    PlaceholderPage({
      title: 'Help Desk',
      description:
        'Reach a BIASafe analyst, raise a support ticket, or browse the knowledge base. Responses within one business hour during the pilot.',
    }),
});

// Placeholder routes for sidebar nav items that point at features
// still under development. Without these, every "Soteria AI",
// "Workspace", "Clients" click in the Figma sidebar 404s, which
// breaks the production-demo flow at the first nav click.
const soteriaRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'soteria',
  component: lazyRouteComponent(
    () => import('@/features/SoteriaAI/SoteriaAIPage'),
    'SoteriaAIPage',
  ),
});

const attachmentFileRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'soteria/attachments/$attachmentId',
  component: lazyRouteComponent(
    () => import('@/features/SoteriaAI/components/AttachmentFilePage'),
    'AttachmentFilePage',
  ),
});

const clientsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'clients',
  component: lazyRouteComponent(
    () => import('@/features/Clients/Clients'),
    'Clients',
  ),
});

const openAccountRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'clients/$clientId/open-account',
  component: lazyRouteComponent(
    () => import('@/features/Clients/OpenAccountPage'),
    'OpenAccountPage',
  ),
});

// THERE IS NO SECOND WAY TO OPEN ACCOUNTS. "New dossier" was a second door
// onto the same job: the advisor chose between two screens that both opened
// accounts, and the one they picked decided which half of the product they got.
// The wizard unfolds instead. Select accounts of more than one kind and it
// grows a tab per application, which is what the dossier was for.
//
// `case-intake` and `case-sign` above are NOT this. They are the CLIENT's side
// of a dossier already sent out, reached by a token in an email that may be in
// somebody's inbox right now, and they stay.
//
// Every `opening-cases` API endpoint stays too, and so does every stored draft,
// packet and packet document. What is gone is a second advisor UI, not a
// record.

const clientDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'clients/$clientId',
  validateSearch: (
    search: Record<string, unknown>,
  ): { thread?: string; tab?: 'alerts' | 'meetings' } => ({
    thread: typeof search.thread === 'string' ? search.thread : undefined,
    tab:
      search.tab === 'alerts' || search.tab === 'meetings'
        ? search.tab
        : undefined,
  }),
  component: lazyRouteComponent(
    () => import('@/features/Clients/ClientDetailPage'),
    'ClientDetailPage',
  ),
});

// Clientless entry into the account-opening flow (the header's Open Account
// CTA) — the flow's first step selects or creates the client. The static
// segment outranks the dynamic /clients/$clientId route.
const openAccountStandaloneRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'clients/open-account',
  component: lazyRouteComponent(
    () => import('@/features/Clients/OpenAccountPage'),
    'OpenAccountPage',
  ),
});

const routeTree = rootRoute.addChildren([
  // The client portal is a SIBLING of the authenticated advisor tree, not a
  // branch of it. Its gate admits a different population and its layout draws
  // different chrome; nesting it under `authRoute` would put a client inside
  // the advisor tree and leave "what can they see?" to conditional rendering.
  ...portalRoutes,
  loginRoute,
  accessRoute,
  signUpRoute,
  verifyEmailRoute,
  authActionRoute,
  securitySetupRoute,
  acceptInvitationRoute,
  forgotPasswordRoute,
  guestTranscriptRoute,
  callBackRoute,
  microsoftCalendarCallbackRoute,
  googleCalendarCallbackRoute,
  microsoftMailCallbackRoute,
  googleMailCallbackRoute,
  microsoftPhoneCallbackRoute,
  microsoftSharepointCallbackRoute,
  microsoftSigninCallbackRoute,
  onboardingRoute,
  hostedOnboardingRoute,
  caseIntakeRoute,
  caseSignRoute,
  cosignRoute,
  authRoute.addChildren([
    indexRoute,
    userProfileRoute,
    dashboardRoute,
    portfolioRoute,
    executionRoute,
    instrumentRoute,
    securityDetailRoute,
    meetingBotsRoute,
    integrationsRoute,
    settingsRoute,
    ...devGalleryRoutes,
    docsRoute,
    changelogRoute,
    helpRoute,
    soteriaRoute,
    attachmentFileRoute,
    clientsRoute,
    openAccountRoute,
    openAccountStandaloneRoute,
    clientDetailRoute,
    teamRoute,
    strategiesRoute.addChildren([
      strategiesDashboardRoute,
      smartStrategyDefinitionFormRoute.addChildren([
        startingUniverseRoute,
        rebalanceScheduleRoute,
        securitySelectionStepRoute,
        weightingMethodRoute,
      ]),
      strategyReportRoute,
      liveModelRoute,
      benchmarkDetailRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  basepath: config.app.basePath,
  context: { ...TanStackQueryProvider.getContext() },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: RouteErrorFallback,
  // A ROUTE THAT IS STILL ARRIVING NOW SAYS SO. Every page here is a
  // `lazyRouteComponent`, so navigation does not commit until the module
  // resolves, and until it commits `useLocation()` still reports the old path
  // — which is what the sidebar's active indicator reads. Without a pending
  // component a slow route left the highlight on the previous item and the
  // content unchanged, so a click looked broken rather than slow.
  defaultPendingComponent: RoutePending,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
